import * as React from 'react';

import { atom, useAtomValue } from 'jotai';

import { useIdentity } from './useIdentity';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { $user } from '~/shared/providers/auth/identity.localState';

type UserFacility = {
  id: string;
  facilityName: string;
};

const userFacilitiesQueryAtom = createQueryAtomFactory(
  'shared.userFacilities',
  (params: { userId: string }) => ({
    coreUsersByOrganization: {
      __args: {
        filter: { ids: [params.userId] },
        pagination: { limit: 1, offset: 0 },
      },
      entries: {
        facilities: {
          id: true,
          facilityName: true,
        },
      },
    },
  }),
  {
    select: (data): UserFacility[] => {
      const user = data?.coreUsersByOrganization?.entries?.[0];
      if (!user?.facilities) return [];
      return user.facilities.map((f: { id: string; facilityName: string }) => ({
        id: f.id,
        facilityName: f.facilityName,
      }));
    },
  },
);

export const $remoteUserFacilities = userFacilitiesQueryAtom((get) => {
  const user = get($user);
  return {
    variables: { userId: user?.id ?? '' },
    skip: !user?.id,
  };
});

const userPreferencesQueryAtom = createQueryAtomFactory(
  'shared.userPreferences',
  (params: { userId: string }) => ({
    userPreferences: {
      __args: { userId: params.userId },
      primaryFacilityId: true,
    },
  }),
  {
    select: (data): string | null =>
      data?.userPreferences?.primaryFacilityId ?? null,
  },
);

export const $remoteUserPreferences = userPreferencesQueryAtom((get) => {
  const user = get($user);
  return {
    variables: { userId: user?.id ?? '' },
    skip: !user?.id,
  };
});

const $userDefaultFacilityId = atom((get) => {
  const result = get($remoteUserFacilities);
  const facilities = result.data ?? [];
  return facilities.length === 1 ? facilities[0].id : undefined;
});

export const useUserDefaultFacilityId = (): string | undefined =>
  useAtomValue($userDefaultFacilityId);

export const useUserFacilities = () => {
  const result = useAtomValue($remoteUserFacilities);
  return {
    facilities: result.data ?? [],
    isLoading: result.isLoading,
  };
};

export const useAutoSelectFacility = (options?: {
  facilities: { id: string }[];
  isLoading: boolean;
}) => {
  const { facilityId, setFacilityId } = useIdentity();
  const userFacilities = useUserFacilities();
  const preferences = useAtomValue($remoteUserPreferences);
  const facilities = options?.facilities ?? userFacilities.facilities;
  const isLoading =
    (options?.isLoading ?? userFacilities.isLoading) || preferences.isLoading;

  React.useEffect(() => {
    if (facilityId || isLoading || facilities.length === 0) return;

    const preferredId = preferences.data;
    const hasPreferred =
      preferredId && facilities.some((f) => f.id === preferredId);

    setFacilityId(hasPreferred ? preferredId : facilities[0].id);
  }, [facilityId, isLoading, facilities, preferences.data, setFacilityId]);
};
